<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { cloneDeep, orderBy } from 'lodash';
import i18n from '@/localisation/i18n';
import PrettyDateTime from '@/modules/common/components/format-date/PrettyDateTime.vue';
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import { DataTableHeader } from 'vuetify';
import { UserNotification } from '@/modules/common/models';
import { useNotifications } from '@/modules/notifications/composables/use-notifications';
import logger from '@/modules/common/services/logger.service';

const maxNotifications = 1000;

const tableColumns: DataTableHeader[] = [
  {
    text: i18n.tc('notificationSubject'),
    value: 'subject',
    class: 'text-no-wrap text-truncate',
    cellClass: 'text-no-wrap text-truncate',
  },
  {
    text: '',
    value: 'createdAt',
    filterable: false,
    align: 'end',
    sort: (a: Date, b: Date) => (a === b ? 0 : a > b ? 1 : -1),
  },
];

const {
  notifications,
  totalNotificationCount,
  fetchMyNotifications,
  markNotificationRead,
  deleteNotifications,
} = useNotifications();

const tableRef = ref<{ multipleSelect: () => void } | null>(null);
const notificationView = ref<UserNotification[]>([]);
const notificationExpanded = ref<UserNotification[]>([]);
const notificationSelected = ref<UserNotification[]>([]);

// Fetch new notifications payload when the count updates (which happens from socket events)
watch(totalNotificationCount, () => {
  void fetchMyNotifications();
});

// Keep the users selection if notifications are updated by the server
watch(notifications, (newNotifications) => {
  notificationView.value = orderBy(cloneDeep(newNotifications), ['id'], ['desc']);
  notificationSelected.value = [];
  // if any of the notificationExpanded is not in notificationView then we reset notificationExpanded
  if (
    !notificationExpanded.value.every((row) => {
      return notificationView.value.some((rowInView) => rowInView.id == row.id);
    })
  ) {
    notificationSelected.value = [];
  }
});

onMounted(async () => {
  // DO NOT REMOVE or EDIT unless you understand
  // https://provablelabs.atlassian.net/browse/AURORA-1480
  if (tableRef.value) {
    tableRef.value.multipleSelect = () => {
      /* */
    };
  }

  try {
    await fetchMyNotifications();
  } catch (e) {
    logger.warn(e);
  }
});

function markNotificationsRead(): void {
  const ids: string[] = [];
  for (const n of notificationSelected.value) {
    if (!isAlreadyRead(n)) {
      ids.push(n.id);
    }
  }
  if (ids.length) {
    void markNotificationRead(ids);
  }
}

function removeNotifications(): void {
  const ids: string[] = [];
  for (const n of notificationSelected.value) {
    ids.push(n.id);
  }
  if (ids.length) {
    // ignore server errors; user can't help it
    void deleteNotifications(ids);
  }
}

function onNotificationDelete(n: UserNotification): void {
  // ignore server errors; user can't help it
  void deleteNotifications([n.id]);
}

function expandRow(row: UserNotification): void {
  // single expand
  void markRead(row);
  notificationExpanded.value = [row];
}

async function markRead(n: UserNotification): Promise<void> {
  if (!isAlreadyRead(n)) {
    // ignore server errors; user can't help it
    await markNotificationRead([n.id]);
  }
}

function isAlreadyRead(n: UserNotification): boolean {
  return !!n.readAt;
}
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <DashboardPanel no-collapse :title="$t('notifications')">
      <div class="flex d-flex flex-column">
        <VDataTable
          ref="tableRef"
          v-model="notificationSelected"
          class="elevation-1 d-flex flex flex-column"
          disable-pagination
          :expanded="notificationExpanded"
          fixed-header
          :headers="tableColumns"
          height="100%"
          hide-default-footer
          :items="notificationView"
          :items-per-page="-1"
          show-expand
          show-select
          single-expand
          sort-by="createdAt"
          sort-desc
          @click:row="expandRow"
          @item-expanded="($event) => expandRow($event.item)"
        >
          <template #top>
            <div class="d-flex flex-row">
              <VToolbar flat>
                <VToolbarTitle>Your Notifications</VToolbarTitle>
                <VDivider class="mx-4" inset vertical />

                <template v-if="!notifications.length">
                  <VAlert border="left" class="mt-4" color="info" dense icon="mdi-information" text>
                    {{ $t('no-notifications') }}
                  </VAlert>
                </template>

                <template v-if="notifications.length >= maxNotifications">
                  <VAlert border="left" class="mt-4" color="gray" dense icon="mdi-information" text>
                    {{
                      $t('more-notifications', {
                        count: totalNotificationCount,
                        max: maxNotifications,
                      })
                    }}
                  </VAlert>
                </template>

                <template v-if="notificationSelected.length">
                  <VBtn color="error" small @click="removeNotifications">
                    <VIcon left>mdi-trash-can-outline</VIcon>
                    {{
                      $tc('deleteSelectedNotifications', notificationSelected.length, {
                        count: notificationSelected.length,
                      })
                    }}
                  </VBtn>
                  <VBtn color="info" small @click="markNotificationsRead">
                    <VIcon left>mdi-message</VIcon>
                    {{
                      $tc('readSelectedNotifications', notificationSelected.length, {
                        count: notificationSelected.length,
                      })
                    }}
                  </VBtn>
                </template>
              </VToolbar>
            </div>
          </template>

          <template #[`item.subject`]="{ item }">
            <span :class="{ 'message-unread': !item.readAt }">{{ item.subject }}</span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            <span :class="{ 'message-unread': !item.readAt }">
              <PrettyDateTime short :when="item.createdAt" />
            </span>
          </template>

          <template #expanded-item="{ headers, item }">
            <td colspan="2" />
            <td :colspan="headers.length - 3">
              <VContainer class="pl-0 pb-5" fluid>
                <VRow no-gutters>
                  <VCol>
                    <div class="preformatted" v-html="item.message" />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VBtn color="error" width="250" @click="onNotificationDelete(item)">
                      <VIcon left>mdi-trash-can-outline</VIcon>
                      {{ $tc('deleteSelectedNotifications', 1, { count: 1 }) }}
                    </VBtn>
                  </VCol>
                </VRow>
              </VContainer>
            </td>
            <td colspan="1" />
          </template>
        </VDataTable>
      </div>
    </DashboardPanel>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  table {
    thead th {
      text-transform: capitalize;
    }
  }

  .preformatted {
    // inline html generated from markdown by the backend
    table {
      width: 600px;
      margin: 20px 0;

      thead th:first-child {
        text-align: left;
      }

      tbody td:first-child {
        width: 250px;
      }
    }

    .hidden-feature {
      display: none;
    }
  }

  .message-unread {
    font-weight: bold;
  }
}
</style>
